import { Box, createStyles, MantineProvider } from "@mantine/core";
import { Flex, IconV2, RpiLink, RpiText, useShowInfoToast } from "@rpi/openapi-core";
import React from "react";
import { Link, Outlet, useNavigate } from "react-router-dom";
import { useAuthUser } from "../../hooks/useAuthUser";
import { getRoutePath } from "../../hooks/useNavigateRoute";
import { RpiNotification, useRpiNotifications } from "../../hooks/useRpiNotifications";
import { canViewPage } from "../../utils/auth.util";
import { Error401View } from "../../views/error/Error401View";
import { optObj } from "../../utils/misc.util";
import { Toggle, tokens } from "@blurb/codex-react";
import { rpaTheme } from "../../theme/rpaTheme";
import { Environment } from "../../env/Environment";
import { NavBar, navCollapsedWidth } from "./NavBar";
import { ConfirmationModalV2 } from "../../components/modals/ConfirmationModalV2";
import { useTypedMatches } from "../../utils/route.util";

const useDashboardStyles = createStyles(() => ({
  dashboardContainer: {
    flex: 1,
    position: "relative",
    overflow: "hidden",
    borderRadius: `${tokens.radius.md} ${tokens.radius.md} 0 0`,
  },
  viewContainer: {
    flex: 1,
    position: "relative",
    background: tokens.color.primitive.light.calm[50],
    marginLeft: navCollapsedWidth,
  },
}));

export const DashboardLayout: React.FC = () => {
  const { classes } = useDashboardStyles();
  const user = useAuthUser();
  const matches = useTypedMatches();
  const navigate = useNavigate();
  const { notifications } = useRpiNotifications();

  const hasTaxNotification = React.useMemo(
    () => notifications[RpiNotification.TAX_DOCUMENT_NOT_UPLOADED],
    [notifications]
  );

  const children = React.useMemo(() => {
    if (!canViewPage(matches, user.cognitoUser)) return <Error401View root={false} hideButton />;
    return <Outlet />;
  }, [matches, user.cognitoUser]);

  function handleSandboxToggle(isSandbox: boolean) {
    user.setIsSandbox(isSandbox);

    const navOnToggleMatch = matches.find((m) => m.handle?.navigateOnSandboxToggle !== undefined);
    if (navOnToggleMatch?.handle?.navigateOnSandboxToggle) {
      navigate(navOnToggleMatch.handle.navigateOnSandboxToggle);
    }
  }

  return (
    <Flex.Column
      sx={(theme) => ({
        width: "100%",
        height: "100vh",
        position: "relative",
        overflow: "hidden",
        backgroundColor: theme.colors.sky[5],
        margin: 0,
        padding: 0,
      })}
    >
      {!Environment.isSandbox && (
        <MantineProvider theme={rpaTheme}>
          <SandboxBanner isSandbox={user.isSandbox} setIsSandbox={handleSandboxToggle} />
        </MantineProvider>
      )}
      <Banner active={Environment.isSandbox}>
        <IconV2.ErrorOutline color="currentColor" size={16} />
        <RpiText type="p3" weight="regular">
          This environment will be deprecated on May 5th, 2026
        </RpiText>
      </Banner>

      <Banner active={!user.isSandbox && hasTaxNotification}>
        <IconV2.ErrorOutline color="currentColor" size={16} />
        <RpiText
          type="p3"
          weight="regular"
          children="With your state reseller's certificate, no sales tax is charged on your orders."
        />
        <RpiLink
          component={Link}
          to={getRoutePath("billing")}
          sx={{ color: "currentcolor" }}
          children="Upload it here"
        />
      </Banner>

      <Flex.Row className={classes.dashboardContainer}>
        <NavBar />

        <Box className={classes.viewContainer}>{children}</Box>
      </Flex.Row>
    </Flex.Column>
  );
};

interface BannerProps {
  active?: boolean;
  border?: boolean;
  children?: React.ReactNode;
}

const Banner: React.FC<BannerProps> = ({ active, border, children }) => {
  return (
    <Flex.Row
      fullWidth
      justify="center"
      align="center"
      gap={16}
      sx={(theme) => ({
        background: theme.colors.spark[2],
        color: theme.colors.spark[6],
        height: 47,
        maxHeight: active ? 47 : 0,
        transition: "max-height ease-in-out 125ms",
        borderBottom: optObj(border, `1px solid ${theme.colors.spark[3]}`),
      })}
    >
      {active && children}
    </Flex.Row>
  );
};

const useSandboxBannerStyles = createStyles(() => ({
  banner: {
    display: "flex",
    alignItems: "center",
    justifyContent: "space-between",
    flexDirection: "row",
    padding: `${tokens.spacing[3]} ${tokens.spacing[4]} ${tokens.spacing[6]} ${tokens.spacing[4]}`,
    marginBottom: `calc(-1 * ${tokens.spacing[3]})`,
    width: "100%",
  },
  bannerSandbox: {
    backgroundColor: tokens.color.semantic.bg.graySubtlerActive,
    color: tokens.color.semantic.text.bold,
  },
  bannerProduction: {
    backgroundColor: tokens.color.primitive.light.fog[950],
    color: tokens.color.semantic.text.inverse,
  },
  leftEnd: {
    display: "flex",
    flexGrow: 1,
    flexBasis: 0,
    justifyContent: "flex-start",
  },
  rightEnd: {
    display: "flex",
    flexGrow: 1,
    flexBasis: 0,
    justifyContent: "flex-end",
  },
  toggleContainer: {
    flexDirection: "row",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    padding: `${tokens.spacing[1]} ${tokens.spacing[3]}`,
    gap: tokens.spacing[1],
    backgroundColor: tokens.color.primitive.light.charcoal[50],
    borderRadius: tokens.radius.sm,
    border: `1px solid ${tokens.color.primitive.light.charcoal[500]}`,
    color: tokens.color.semantic.text.bold,
  },
}));

type SandboxBannerProps = {
  isSandbox: boolean;
  setIsSandbox: (value: boolean) => void;
};
const SandboxBanner: React.FC<SandboxBannerProps> = ({ isSandbox, setIsSandbox }) => {
  const { classes } = useSandboxBannerStyles();
  const [showConfirm, setShowConfirm] = React.useState(false);
  const showInfoToast = useShowInfoToast();

  const getSandboxBannerMessage = (isSandbox: boolean): string => {
    return isSandbox
      ? "You're testing in Sandbox - your place to experiment with orders"
      : "You're now in Production - all orders and payments are live!";
  };

  const SwitchEnvironmentConfirmation = (
    <ConfirmationModalV2
      opened={showConfirm}
      onClose={() => setShowConfirm(false)}
      onConfirm={() => {
        const newIsSandbox = !isSandbox;
        setIsSandbox(newIsSandbox);
        setShowConfirm(false);
        showInfoToast(newIsSandbox ? "You're now in Sandbox" : "You're now in Production");
      }}
      title={isSandbox ? "Switch to Production" : "Switch to Sandbox"}
      message={
        isSandbox
          ? "You are about to switch to Production. This means all activity will affect live orders and real customers."
          : "You are about to switch to Sandbox. Here you can test features and workflows safely. No live orders or customer data will be affected."
      }
      confirmLabel={"Confirm"}
      cancelLabel={"Cancel"}
    />
  );

  return (
    <div className={`${classes.banner} ${isSandbox ? classes.bannerSandbox : classes.bannerProduction}`}>
      {SwitchEnvironmentConfirmation}
      <RpiText type="p3" weight="bold" className={classes.leftEnd}>
        {isSandbox ? "SANDBOX" : "PRODUCTION"}
      </RpiText>
      <RpiText type="p3" weight="regular">
        {getSandboxBannerMessage(isSandbox)}
      </RpiText>

      <div className={classes.rightEnd}>
        <Box className={classes.toggleContainer} aria-label="Toggle between Sandbox and Production environments">
          <RpiText type="p3" weight="bold" id="sandbox-label">
            Sandbox
          </RpiText>
          <Toggle
            pressed={!isSandbox}
            data-testid="sandboxToggle"
            onPressedChange={() => setShowConfirm(true)}
            aria-label="Switch between Sandbox and Production mode"
            aria-describedby="sandbox-label production-label"
          />
          <RpiText type="p3" weight="bold" id="production-label">
            Production
          </RpiText>
        </Box>
      </div>
    </div>
  );
};
